import React, { memo, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../../constants/styles';
import { CTAButton, Navigation, I18n } from '../../components';
import moment from 'moment';
import {
  EVENT_TYPE_CMR,
  EVENT_TYPE_CMR_FLY,
  EVENT_TYPE_READINESS,
} from '../../../constants/eventTypes';
import { SUCCESS } from '../../../actions/statusTypes';

import { dateIsPast } from '../../components/schedule_cal';
import AppointmentsCal from '../scheduler/appointments_cal';
import HoverOverView from './cal_hover_over_view';

const AppointmentCalPublic = memo((props) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [selectedRange, setSelectedRange] = useState(null);
  const [hoverOverDate, setHoverOverDate] = useState(null);
  const [hoverOverTop, setHoverOverTop] = useState(100);
  const [hoverOverLeft, setHoverOverLeft] = useState(100);

  const calRef = useRef();

  useEffect(() => {
    if (props.calUnavailableStatus === SUCCESS) {
      calRef.current?.resetSelectedTimes();
    }
  }, [props.calUnavailableStatus]);
  useEffect(() => {
    if (props.aptsRequestStatus === SUCCESS) {
      calRef.current?.resetSelectedTimes();
    }
  }, [props.aptsRequestStatus]);

  const handleSelectedDate = useCallback(
    (date) => {
      setSelectedDate(date);
      props.onSelectedDate?.(date);
    },
    [props.onSelectedDate]
  );

  const availableTimesByDates = useMemo(() => {
    const availableTimes = (props.slotAvailability?.available || []).reduce((acc, dStr) => {
      const dateKey = dStr.split('T')[0];
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(dStr.split('T')[1]);
      return acc;
    }, {});
    const unavailableTimes = (props.slotAvailability?.unavailable || []).reduce((acc, dStr) => {
      const dateKey = dStr.split('T')[0];
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(dStr.split('T')[1]);
      return acc;
    }, {});
    return {
      available: availableTimes,
      unavailable: unavailableTimes,
    };
  }, [props.slotAvailability]);

  const isUnscheduledDay = useCallback(
    (date) => {
      if (props.eventType === EVENT_TYPE_READINESS) return false;
      const availableTimes = availableTimesByDates.available[moment(date).format('YYYY-MM-DD')];
      const unavailableTimes = availableTimesByDates.unavailable[moment(date).format('YYYY-MM-DD')];
      return !availableTimes?.length && !unavailableTimes?.length;
    },
    [props.slotAvailability, props.eventType]
  );
  const isFull = useCallback(
    (date) => {
      if (props.eventType === EVENT_TYPE_READINESS) return false;
      const availableTimes = availableTimesByDates.available[moment(date).format('YYYY-MM-DD')];
      return !availableTimes?.length;
    },
    [props.slotAvailability, props.eventType]
  );
  const isBlocked = useCallback(
    (date) => {
      if (props.eventType === EVENT_TYPE_READINESS) return false;
      return isFull(date) && !props.eventsByStartDate?.[moment(date).format('YYYY-MM-DD')]?.length;
    },
    [isFull, props.eventsByStartDate, props.eventType]
  );
  const isInSelected = useCallback(
    (date) => {
      if (!selectedRange || !selectedRange.length) return false;
      const rangeStartMoment = moment(selectedRange[0]);
      const rangeEndMoment = moment(selectedRange[selectedRange.length - 1]);
      const dateMoment = moment(date);
      return (
        dateMoment.isSame(rangeStartMoment) ||
        dateMoment.isSame(rangeEndMoment) ||
        dateMoment.isBetween(rangeStartMoment, rangeEndMoment)
      );
    },
    [selectedRange]
  );

  const canSelectSlot = useCallback((start) => {
    return true;
  }, []);

  const calDisplayTextAsOffRangeForDate = useCallback(
    (date) => {
      return !!dateIsPast(date) || !!isUnscheduledDay(date) || !!isFull(date);
    },
    [isFull, isUnscheduledDay]
  );
  const calCellBGClassForDate = useCallback(
    (date) => {
      let className = '';
      let showAsFull = isFull(date) && !isUnscheduledDay(date) && !isBlocked(date);
      let isSelectedOrToday = isInSelected(date) || date.getTime() === selectedDate?.getTime();
      if (!isSelectedOrToday) {
        if (showAsFull) className += ' rbc-disabled-bg';
        else if (isBlocked(date)) className += ' rbc-blocked-bg';
      } else {
        className += ' rbc-today';
      }
      return className;
    },
    [isFull, isUnscheduledDay, isBlocked, selectedDate]
  );

  const handleScheduleProviderAvailability = useCallback(() => {
    Navigation.showModal({
      screen: 'ProviderAvailabilityOverride',
      title: 'Appointment Availability',
      passProps: {
        selectedDate,
        selectedTimes: selectedTimes?.length ? selectedTimes : null,
        selectedRange,
        eventType: props.eventType,
      },
      options: {
        width: 850,
      },
    });
    calRef.current?.resetSelectedTimes();
  }, [selectedDate, selectedTimes, selectedRange, props.eventType]);

  const handleScheduleReadiness = useCallback(() => {
    if (props.eventType === EVENT_TYPE_READINESS) {
      const subScreen = 'DeploymentForm';
      const title = I18n.ac('airforce.deploymentInfo');
      let passProps = {
        selectedCases: [],
        eventType: EVENT_TYPE_READINESS,
        startDate: selectedDate,
      };

      let screen;
      if (__WEB__) {
        passProps = {
          ...passProps,
          subScreen: {
            screen: subScreen,
            title,
            passProps,
          },
        };
        screen = 'SelectSplitScreen';
      } else {
        screen = 'SelectCasesAndGroups';
        passProps = {
          ...passProps,
          screenTitle: title,
        };
      }
      Navigation.showModal({
        screen,
        title,
        passProps,
      });
      return;
    }
  }, [selectedDate, props.eventType]);

  const handleSchedule = useCallback(() => {
    if (props.schedulingForCaseId) {
      props.openCaseInfo(props.schedulingForCaseId);
    }
    Navigation.showModal({
      screen: 'CreatePHARequest',
      title: 'Appointment Info',
      passProps: {
        selectedDate,
        selectedTimes: [EVENT_TYPE_CMR_FLY, EVENT_TYPE_CMR].includes(props.eventType)
          ? ['07:30']
          : selectedTimes,
        allDay: false,
        admin: true,
        eventType: props.eventType,
        readOnlyUnlessMissing: false,
      },
    });
    calRef.current?.resetSelectedTimes();
  }, [selectedDate, selectedTimes, props.eventType, props.schedulingForCaseId, props.openCaseInfo]);

  // const onHoverIn = useCallback(({ startDate, viewLayout }) => {
  //   if (dateIsPast(startDate)) return;
  //   setHoverOverDate(startDate ? moment(startDate).format('YYYY-MM-DD') : null);
  //   setHoverOverTop(viewLayout?.top);
  //   setHoverOverLeft(viewLayout?.left);
  // }, []);
  // const onHoverOut = useCallback(() => {
  //   setHoverOverDate(null);
  //   setHoverOverTop(100);
  //   setHoverOverLeft(100);
  // }, []);

  const renderedButtons = () => {
    if (!props.canEdit) return null;
    if (props.eventType === EVENT_TYPE_READINESS) {
      return (
        <View
          key={'schedule'}
          style={{ marginLeft: 25, width: 300, marginTop: 20, marginBottom: 20 }}
        >
          <CTAButton
            label="Schedule"
            styleType="main"
            onPress={handleScheduleReadiness}
            disabled={false}
          />
        </View>
      );
    }
    if (isUnscheduledDay(selectedDate)) return null;
    const numTimesSelected = selectedTimes.length;
    return (
      <View key={'schedule'} style={styles.footer}>
        <CTAButton
          label={'Provider Availability'}
          styleType="secondary"
          onPress={handleScheduleProviderAvailability}
          disabled={false}
        />
        <View style={{ flex: 1 }} />
        <CTAButton
          label="Schedule"
          styleType="main"
          onPress={handleSchedule}
          disabled={
            ![EVENT_TYPE_CMR_FLY, EVENT_TYPE_CMR].includes(props.eventType) &&
            (!numTimesSelected || numTimesSelected > 1)
          }
        />
      </View>
    );
  };

  return (
    <View style={[styles.paneWrapper, { flex: 1 }]}>
      <AppointmentsCal
        ref={calRef}
        selectedDate={selectedDate}
        onSelectedDate={handleSelectedDate}
        onSelectedRange={setSelectedRange}
        onTimesChange={setSelectedTimes}
        events={props.events}
        calDisplayTextAsOffRangeForDate={calDisplayTextAsOffRangeForDate}
        calCellBGClassForDate={calCellBGClassForDate}
        slotAvailability={props.slotAvailability}
        eventType={props.eventType}
        multi={props.eventType !== EVENT_TYPE_READINESS}
        disablePastSelect={false}
        canSelectSlot={canSelectSlot}
        fetchStatus={props.aptsSlotAvailabilityStatus}
        dotStyle={styles.dot}
        admin={true}
        hideTimes={[EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY].includes(props.eventType)}
      />

      {renderedButtons()}
      {/*
        <HoverOverView
          availableTimes={
            availableTimesByDates.available[moment(hoverOverDate).format('YYYY-MM-DD')]
          }
          eventType={props.eventType}
          top={hoverOverTop}
          left={hoverOverLeft}
        />
      */}
    </View>
  );
});

const styles = StyleSheet.create({
  paneWrapper: {
    flex: 1,
  },
  hoverOverView: {
    alignItems: 'flex-end',
    position: 'absolute',
    minWidth: 100,
    minHeight: 50,
    backgroundColor: 'rgba(22, 33, 54, .8)',
    borderRadius: 10,
    padding: 15,
  },
  hoverOverItemText: {
    ...FONT.medium,
    color: '#fefefe',
    fontSize: 12,
  },
  footer: {
    ...THEME.basicRow,
    paddingHorizontal: __WEB__ ? 25 : 10,
    paddingBottom: 30,
    marginTop: 15,
  },
});

export default AppointmentCalPublic;
